/**
 * Where focus goes after the dialog opens or navigates (Option C, C1, plan §3.6 "Focus after navigation"):
 * - `filter`: the start view's filter (open on the start view, All engines, Escape back);
 * - `name`: Name (a manual pick from a card, the filter's Enter, the rail or the compact select; edit mode opening);
 * - `prefill`: the first applicable, visible, required, EMPTY field per the field rules (a paste, a palette or SAS
 *   prefill, an Azure pick) — e.g. Password after an ADO.NET string without one — falling back to Name;
 * - `paste-entry`: the Paste connection string entry the form view's paste field was opened from, when it closes (the
 *   rail's row, or the compact bar's Add from… button — C3), falling back to Name.
 * Fields render only while their rule applies, so "visible" is "in the document and enabled".
 */
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import { requiredEmptyFields } from '../state/connectionFieldRules';
import { revealControl } from '../pane/formSectionReveal';

export type FocusTarget = 'filter' | 'name' | 'prefill' | 'paste-entry';

function focusable(selector: string): HTMLElement | null {
  const element = document.querySelector<HTMLElement>(selector);
  // A collapsed form section is not "hidden" for this purpose: it stays mounted and opens on focus (C2).
  if (!element || (element as HTMLInputElement).disabled || element.closest('[hidden]:not([data-form-section-body])')) return null;
  return element;
}

/** The element focus goes to for `target` with these values, or null when the view is not rendered yet. */
export function dialogFocusElement(target: FocusTarget, values: Partial<ConnectionFormValues>): HTMLElement | null {
  if (target === 'filter') return focusable('[data-testid="engine-filter-input"]');
  if (target === 'paste-entry') {
    const entry = focusable('[data-testid="rail-entry-points"] [data-testid="paste-connection-string-button"]') ?? focusable('[data-testid="compact-add-from-button"]');
    if (entry) return entry;
  }
  if (target === 'prefill') {
    for (const rule of requiredEmptyFields(values)) {
      const element = focusable(rule.selector);
      if (element) return element;
    }
  }
  return focusable('#alias');
}

export function focusDialogTarget(target: FocusTarget, values: Partial<ConnectionFormValues>): void {
  const element = dialogFocusElement(target, values);
  revealControl(element, () => element?.focus({ preventScroll: false }));
}

/**
 * Focus the entry point a nested flow was opened from (C3, plan §3.6 "nested focus return"): the rail's row, or — after
 * a resize — its mapped equivalent (the compact bar's Add from…); on the start view the filter, where the combobox
 * keeps the entry as its active option.
 */
export function focusEntryPoint(entryTestId: string): void {
  const element =
    focusable(`[data-testid="rail-entry-points"] [data-testid="${entryTestId}"]`) ??
    focusable('[data-testid="compact-add-from-button"]') ??
    focusable('[data-testid="engine-filter-input"]') ??
    focusable('#alias');
  element?.focus({ preventScroll: true });
}
